/* eslint-disable import/no-cycle */
import { call, put, all, takeLatest } from 'redux-saga/effects';
import {
    IApplyToAudition,
    ICreateAuditionPayload,
    ICreateCastingDirector,
    IGetAudition,
    IGetSimilarAuditions,
    IUpdateAudition,
    IUpdateCastingDirector,
    IManageAuditionParams,
    types,
    IManageAuditionResponse,
    IUpdateRole,
} from './types';
import {
    getAllAuditionsSuccess,
    getAuditionsDetailsFailed,
    getAuditionsDetailsSuccess,
    getAllAuditionsFailed,
    applyToAuditionSuccess,
    applyToAuditionFailed,
    getAuditionRolesSuccess,
    getAuditionRolesFailed,
    getMyApplicationsSuccess,
    getMyApplicationsFailed,
    getSavedCollectionSuccess,
    getSavedCollectionFail,
    saveCollectionSuccess,
    saveCollectionFail,
    deleteSavedCollectionSuccess,
    deleteSavedCollectionFail,
    updateSavedCollectionSuccess,
    updateSavedCollectionFail,
    getSavedCollection as getSavedCollectionAction,
    createAuditionSuccess,
    createAuditionFail,
    createRoleSuccess,
    createRoleFail,
    updateAudition as updateAuditionAction,
    updateAuditionSuccess,
    updateAuditionFail,
    publishAuditionSuccess,
    publishAuditionFail,
    deleteAuditionSuccess,
    deleteAuditionFail,
    deleteRoleSuccess,
    deleteRoleFail,
    getAllApplicationForAuditionSuccess,
    getAllApplicationForAuditionFail,
    sortApplicationSuccess,
    sortApplicationFail,
    unPublishAuditionSuccess,
    unPublishAuditionFail,
    filterAuditionSuccess,
    filterAuditionFail,
    auditionAdvancedSearchSuccess,
    auditionAdvancedSearchFail,
    LoadMoreAction,
    loadMoreAuditionSuccess,
    loadMoreAuditionFail,
    withdrawApplicationSuccess,
    withdrawApplicationFail,
    IWithdrawApplication,
    getSimilarAuditions as getSimilarAuditionsAction,
    getSimilarAuditionsSuccess,
    getSimilarAuditionsFail,
    getCastingDirectorsSuccess,
    getCastingDirectorsFail,
    createCastingDirectorSuccess,
    createCastingDirectorFail,
    updateCastingDirector as updateCastingDirectorAction,
    updateCastingDirectorSuccess,
    updateCastingDirectorFail,
    deleteCastingDirectorSuccess,
    deleteCastingDirectorFail,
    publishAudition as publishAuditionAction,
    IGetAuditions,
    manageAuditionSuccess,
    manageAuditionFail,
    updateRoleSuccess,
    updateRoleFail,
} from './actions';
import { Action } from 'redux';
import { api } from '../../configurations/api';
import {
    getAuditionUrl,
    getApplicationsForOneAuditionUrl,
    applyToAuditionUrl,
    getAuditionRoleUrl,
    getMyApplicationsUrl,
    getSavedCollectionUrl,
    saveCollectionUrl,
    getMycastingDirectorProfileurl,
    castingDirectorUrl,
} from '../../configurations/api/url';
import { getCurrentUsersAuditions } from '../account/actions';
import { globalErrorHandler } from '../error/saga';
import { httpRequest } from '../types';
import { SentryCapture } from '../../analytics/Sentry';
import { getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import { CACHE_TYPE, NotUniqueCacheValue } from '../cache/types';
import { setCache } from '../cache/action';
import { isArray, isObject } from 'lodash';
import { uploadFilesSaga } from '../upload/saga';
import { ICastingDirector, IGenericDocumentModifier, IProject } from '../../types/global/helper';
import { isEmpty } from '../../utils/lodash';
import { showToastMessage, transformObjectOrArray } from '../../utils/AppUtils';
import { toggleSubscriptionModal } from '../appConfigurator/action';

function filterUploadFiles(payload: any) {
    // Define upload and body field mappings
    const uploadFields = ['profile_picture', 'gallery'];
    const bodyFields = [
        'company_name',
        'date_of_formation',
        'production_type',
        'location',
        'about',
        'email',
        'phone_number',
        'values',
        'perks',
        'avg_salary',
        'timezone',
        'company_videos',
        'company_type',
        'industry',
        'production_type',
        'employee_count',
        'revenue_range',
    ];

    // Initialize transformed upload fields
    const load: any = {};

    // Normalize uploads from various aliases
    Object.keys(payload).forEach(key => {
        switch (key) {
            case 'profile_picture':
            case 'singleRawFile':
                load['profile_picture'] = payload.singleRawFile || payload.profile_picture || [];
                break;
            case 'gallery':
            case 'rawFiles':
                load['gallery'] = payload.rawFiles || payload.gallery || [];
                break;
        }
    });

    // Extract upload fields
    const uploads = Object.keys(load)
        .filter(key => uploadFields.includes(key))
        .reduce((acc: any, key) => {
            acc[key] = load[key];
            return acc;
        }, {});

    // Extract files from upload fields
    const profilePicture = load.profile_picture || [];
    const gallery = load.gallery || [];
    const files = [...profilePicture, ...gallery];

    // Extract body fields
    const body: any = Object.keys(payload)
        .filter(key => bodyFields.includes(key))
        .reduce((acc: any, key) => {
            acc[key] = payload[key];
            return acc;
        }, {});

    // Map ranges
    if (Array.isArray(payload.revenue_range)) {
        [body.min_revenue, body.max_revenue] = payload.revenue_range;
    }

    if (Array.isArray(payload.employee_count)) {
        [body.min_employees, body.max_employees] = payload.employee_count;
    }

    if (Array.isArray(payload.company_videos)) {
        body['company_videos'] = payload.company_videos.map((vid: IProject) => {
            return {
                _id: vid._id,
            };
        });
    }

    return {
        uploads,
        files,
        body,
        profilePicture,
        gallery,
    };
}

/**
 * This behind the hood creates a production company
 */
function* createCastingDirector({ payload }: { payload: ICreateCastingDirector }): any {
    const { body, gallery, profilePicture } = filterUploadFiles(payload);
    try {
        /** Filter out the upload fields */
        const response = yield call(api, castingDirectorUrl, httpRequest.POST, transformObjectOrArray(body), 3, 1000);
        const { data } = response.data;
        const { _id } = data;
        const profileUrl = yield call(uploadFilesSaga, {
            payload: {
                collection: 'company',
                directory: 'media',
                path: 'images',
                files: profilePicture,
                id: _id,
            },
        });
        const galleryUrl = yield call(uploadFilesSaga, {
            payload: { collection: 'company', directory: 'media', path: 'gallery', files: gallery, id: _id },
        });
        yield put(createCastingDirectorSuccess(data));
        yield put(
            updateCastingDirectorAction({
                id: _id,
                body: {
                    profile_picture: profileUrl[0],
                    gallery: galleryUrl,
                },
            }),
        );
        yield call(showToastMessage, 'Company created successfully', 'success');
        return data;
    } catch (error: any) {
        yield put(createCastingDirectorFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateCastingDirector({ payload }: { payload: IUpdateCastingDirector }): Generator<any, any, any> {
    try {
        /** Filter out the upload fields */
        const response = yield call(
            api,
            `${castingDirectorUrl}/${payload.id}`,
            httpRequest.PATCH,
            payload.body,
            3,
            1000,
        );
        const { data } = response.data;
        yield put(updateCastingDirectorSuccess(data));
    } catch (error: any) {
        yield put(updateCastingDirectorFail(error));
    }
}

function* deleteCastingDirector({ payload }: { payload: IGenericDocumentModifier }): Generator<any, any, any> {
    try {
        /** Filter out the upload fields */
        yield call(api, `${castingDirectorUrl}/${payload.documentId}`, httpRequest.DELETE, null, 3, 1000);
        yield put(deleteCastingDirectorSuccess(payload));
    } catch (error: any) {
        yield put(deleteCastingDirectorFail(error));
    }
}

function* getMyCastingDirectors(): any {
    try {
        const response = yield call(api, getMycastingDirectorProfileurl, httpRequest.GET, null, 2, 1000);
        const { data } = response.data;
        yield put(getCastingDirectorsSuccess(data));
    } catch (error: any) {
        yield put(getCastingDirectorsFail(error));
    }
}
function* loadingMoreAuditions({ payload }: { payload: LoadMoreAction }): any {
    try {
        const request = `${getAuditionUrl}?limit=${payload.limit}&page=${payload.page}`;
        const response = yield call(api, request, httpRequest.GET, null, 0, 0);
        yield put(loadMoreAuditionSuccess(response));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(loadMoreAuditionFail(error));
    }
}

function* filterAudition({ payload }: any): any {
    try {
        const request = `${getAuditionUrl}/filter`;
        const response = yield call(api, request, httpRequest.POST, payload, 3, null);
        yield put(filterAuditionSuccess(response));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(filterAuditionFail(error));
    }
}

function* advancedSearchAudition({ payload, resolve, reject }: any): any {
    try {
        const request = `${getAuditionUrl}/filter`;
        const response = yield call(api, request, httpRequest.POST, payload, 3, null);
        const { data } = response;
        yield put(auditionAdvancedSearchSuccess(data));
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(auditionAdvancedSearchFail(error));
        reject(error);
    }
}

function* getAllApplicationsForAudition({ payload, resolve, reject }: any): any {
    const request = getApplicationsForOneAuditionUrl.replace('#id#', payload);

    try {
        const response = yield call(api, request, httpRequest.GET, null, 3, null);
        const { data } = response;
        yield put(getAllApplicationForAuditionSuccess(data));
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getAllApplicationForAuditionFail(error));
        reject(error);
    }
}

function* sortApplication({ payload, resolve, reject }: any): any {
    const { id, sortType } = payload;
    const request = `${applyToAuditionUrl}/${id}`;
    try {
        const response = yield call(api, request, httpRequest.PATCH, { filing_status: sortType }, 0, 0);
        const { data } = response.data;
        yield put(sortApplicationSuccess(data));
        yield call(showToastMessage, 'Application sorted successfully', 'success');
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(sortApplicationFail(error));
        yield call(globalErrorHandler, error);
        reject(error);
    }
}

function* withdrawApplication({
    payload,
    resolve,
    reject,
}: {
    payload: IWithdrawApplication;
    resolve: any;
    reject: any;
}): any {
    const { applicationId } = payload;
    const request = `${applyToAuditionUrl}/${applicationId}/withdraw`;
    try {
        const response = yield call(api, request, httpRequest.DELETE, {}, 0, 0);
        yield put(withdrawApplicationSuccess(payload));
        if (resolve) resolve(response.data);
        yield call(showToastMessage, 'Applicatin successfully withdrawn', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(withdrawApplicationFail(error));
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

/** This should remove the production_company as we only need it in array when creating
 * This is also added in BE aftr creation
 */
function removeProductionCompany(arr: any[]) {
    return arr.map(item => {
        const { production_company, ...rest } = item;
        return rest;
    });
}

const parseAuditionPayload = (payload: ICreateAuditionPayload): Record<any, any> => {
    const final: any = { audition: {}, role: [], castingDirector: {}, imageCover: null, attachment: null };

    Object.keys(payload).forEach(key => {
        const currentKey = key as keyof ICreateAuditionPayload;
        switch (key) {
            case 'role':
                final.role = removeProductionCompany(payload[currentKey] as any[]);
                break;
            case 'production_company':
                if (isObject(payload[currentKey]) && '_id' in (payload[currentKey] as Record<string, any>)) {
                    const item = payload[currentKey] as { _id: string };
                    final.audition[currentKey] = item._id as unknown as ICastingDirector;
                } else {
                    final.castingDirector = payload[currentKey] as ICastingDirector;
                }
                break;
            case 'attachment':
                final.attachment = [payload[currentKey]];
                break;
            case 'imageCover':
                final.imageCover = payload[currentKey];
                break;
            default:
                if (
                    payload &&
                    payload[currentKey] &&
                    isObject(payload[currentKey]) && // Ensure it's an object
                    'value' in (payload[currentKey] as Record<string, any>) // Type assertion for safe access
                ) {
                    final.audition[currentKey] = (payload[currentKey] as { value: any }).value;
                } else {
                    final.audition[currentKey] = payload[currentKey];
                }
                break;
        }
    });

    return final;
};

function* createAudition({ payload }: { payload: ICreateAuditionPayload }): any {
    /** Check and create Roles If Any */
    const { audition, role, castingDirector, imageCover, attachment } = parseAuditionPayload(payload);
    /** Check and create Casting Director if any */
    try {
        const initialPayload = { ...audition };
        if (!isEmpty(castingDirector)) {
            /** trigger castingDirector Creator */
            const data = yield call(createCastingDirector, { payload: castingDirector });
            initialPayload.production_company = data._id;
        }

        const response = yield call(
            api,
            getAuditionUrl,
            httpRequest.POST,
            transformObjectOrArray(initialPayload),
            0,
            0,
        );
        const { data } = response.data;
        const { _id } = data;

        let createdRoles: any[] = [];
        if (role && Array.isArray(role)) {
            // Create roles and collect the created role data
            const roleCreationPromises = role.map(singleRole =>
                call(createAuditionRole, { payload: { auditionId: _id, body: singleRole } }),
            );
            createdRoles = yield all(roleCreationPromises);
        }

        let imageUpload;
        let attachmentUpload;
        if (imageCover && imageCover !== 'no-image' && Array.isArray(imageCover)) {
            imageUpload = yield call(uploadFilesSaga, {
                payload: { collection: 'audition', directory: 'media', path: 'images', files: imageCover, id: _id },
            });
        }

        if (attachment && Array.isArray(attachment)) {
            attachmentUpload = yield call(uploadFilesSaga, {
                payload: { collection: 'audition', directory: 'media', path: 'attachment', files: attachment, id: _id },
            });
        }
        if (imageUpload || attachmentUpload) {
            const body: any = {};

            if (attachmentUpload) {
                body.attachment = attachmentUpload[0];
            }

            if (imageUpload) {
                body.imageCover = imageUpload[0];
            }

            const updatePayload: IUpdateAudition = {
                id: _id,
                body,
                isImageUpload: false,
                fromCreate: true,
                withPublish: false,
            };

            if (payload.publishStatus === 'publish') {
                if (!imageCover) {
                    yield put(publishAuditionAction({ auditionId: _id, roles: createdRoles }));
                } else {
                    updatePayload.withPublish = true;
                    // Pass the created roles to the update action so it can publish with roles
                    updatePayload.createdRoles = createdRoles;
                }
            }
            yield put(updateAuditionAction(updatePayload));
        } else {
            yield put(createAuditionSuccess(data));
            if (payload.publishStatus === 'publish') {
                yield put(publishAuditionAction({ auditionId: _id, roles: createdRoles }));
            }
            yield call(showToastMessage, 'Audition created successfully', 'success');
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createAuditionFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* createAuditionRole({ payload }: any): any {
    const requestUrl = `${getAuditionUrl}/${payload.auditionId}/roles`;
    /** Parse Body */
    const body = { ...payload.body };
    if (payload.body.age_range && isArray(payload.body.age_range)) {
        body.min_age = payload.body.age_range[0];
        body.max_age = payload.body.age_range[1];
        body.age_range = payload.body.age_range.join('-');
    }

    if (payload.body.character && Array.isArray(payload.body.character)) {
        body.character = payload.body.character[0];
    }

    try {
        const response = yield call(api, requestUrl, httpRequest.POST, transformObjectOrArray(body), 0, 0);
        const { data } = response.data;
        yield put(createRoleSuccess(data));
        return data; // Return the created role data
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createRoleFail(error));
        yield call(globalErrorHandler, error);
        throw error; // Re-throw the error so it can be caught by the calling function
    }
}

function* updateAuditionRole({ payload }: { payload: IUpdateRole }): any {
    const requestUrl = `${getAuditionUrl}/${payload.id}/roles`;
    /** Parse Body */
    const body = { ...payload.body };

    if (payload.body.character && Array.isArray(payload.body.character)) {
        body.character = payload.body.character[0];
    }

    try {
        const response = yield call(
            api,
            `${requestUrl}/${payload.id}`,
            httpRequest.PATCH,
            transformObjectOrArray(body),
            0,
            0,
        );
        const { data } = response.data;
        yield put(updateRoleSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        yield put(updateRoleFail(error));
    }
}

function* updateAudition({ payload }: { payload: IUpdateAudition }): any {
    const { isImageUpload, id, body, fromCreate, withPublish, createdRoles: passedCreatedRoles } = payload;
    let requestUrl = `${getAuditionUrl}/${id}`;

    try {
        if (fromCreate) {
            const response = yield call(api, requestUrl, httpRequest.PATCH, body, 0, 0, !!isImageUpload);
            const { data } = response;
            yield put(createAuditionSuccess(data));
            yield call(showToastMessage, 'Audition created successfully', 'success');
            if (payload.withPublish) {
                yield put(publishAuditionAction(id));
            }
        } else {
            const { audition, role, castingDirector, imageCover, attachment } = parseAuditionPayload(body);
            const initialPayload = { ...audition };
            let imageUpload;
            let attachmentUpload;
            if (imageCover) {
                imageUpload = yield call(uploadFilesSaga, {
                    payload: { collection: 'audition', directory: 'media', path: 'images', files: imageCover, id: id },
                });
            }
            if (attachment) {
                attachmentUpload = yield call(uploadFilesSaga, {
                    payload: {
                        collection: 'audition',
                        directory: 'media',
                        path: 'attachment',
                        files: attachment,
                        id: id,
                    },
                });
            }

            if (!isEmpty(castingDirector)) {
                /** trigger castingDirector Creator */
                const data = yield call(createCastingDirector, { payload: castingDirector });
                initialPayload.production_company = data._id;
            }

            let createdRoles: any[] = [];
            if (role && Array.isArray(role)) {
                // Create roles and collect the created role data
                const roleCreationPromises = role.map(singleRole =>
                    call(createAuditionRole, { payload: { auditionId: id, body: singleRole } }),
                );
                createdRoles = yield all(roleCreationPromises);
            }

            if (attachmentUpload) {
                initialPayload.attachment = attachmentUpload[0];
            }

            if (imageUpload) {
                initialPayload.imageCover = imageUpload[0];
            }

            const response = yield call(api, requestUrl, httpRequest.PATCH, initialPayload, 0, 0);
            const { data } = response.data;
            yield put(updateAuditionSuccess(data));
            yield call(showToastMessage, 'Audition updated successfully', 'success');

            // If withPublish is true and roles were created, publish with roles
            // Use passedCreatedRoles if available (from create action), otherwise use createdRoles from this update
            const rolesToPublish = passedCreatedRoles || createdRoles;
            if (withPublish && rolesToPublish.length > 0) {
                yield put(publishAuditionAction({ auditionId: id, roles: rolesToPublish }));
            }
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateAuditionFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* publishAudition({ payload }: any): any {
    // Handle both old format (string) and new format (object with auditionId and roles)
    const auditionId = typeof payload === 'string' ? payload : payload.auditionId;
    const roles = typeof payload === 'string' ? undefined : payload.roles;

    const requestUrl = `${getAuditionUrl}/publish/${auditionId}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.PATCH, {}, 0, 0);
        const { data } = response;

        // If roles were provided, include them in the success payload
        const successPayload = roles ? { ...data, roles } : data;
        yield put(publishAuditionSuccess(successPayload));
        yield put(getCurrentUsersAuditions());
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(publishAuditionFail(error));
        SentryCapture(error, 'error');
        const { message } = error.data || {};
        // TODO Obfusctae this message and use a code instead
        if (message.startsWith('You have reached')) {
            yield put(toggleSubscriptionModal(true));
        }
        yield call(globalErrorHandler, error);
    }
}

function* unPublishAudition({ payload }: any): any {
    const requestUrl = `${getAuditionUrl}/unpublish/${payload}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.PATCH, {}, 0, 0);
        const { data } = response;
        yield put(unPublishAuditionSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(unPublishAuditionFail(error));
    }
}

function* deleteAudition({ payload }: any): any {
    const requestUrl = `${getAuditionUrl}/${payload}`;
    try {
        yield call(api, requestUrl, httpRequest.DELETE, {}, 0, 0);
        yield put(deleteAuditionSuccess(payload));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteAuditionFail(error));
    }
}

function* deleteAuditionRole({ payload }: any): any {
    const requestUrl = `${getAuditionRoleUrl}/${payload}`;
    try {
        yield call(api, requestUrl, httpRequest.DELETE, {}, 0, 0);
        yield put(deleteRoleSuccess(payload));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteRoleFail(error));
    }
}

function* getAllAuditions({ payload }: { payload: IGetAuditions }): Generator<any, any, any> {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;
    const queryParams = new URLSearchParams();
    if (payload && payload.limit) queryParams.append('limit', String(payload.limit));
    if (payload && payload.page) queryParams.append('page', String(payload.page));
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(
        CACHE_TYPE.AUDITION,
        queryParams.toString() as string,
    );
    if (cache && cache.key) {
        initialResult = cache.value;
    }
    if (initialResult && defaultUseCache) {
        yield put(getAllAuditionsSuccess(initialResult));
    } else {
        try {
            const response = yield call(
                api,
                `${getAuditionUrl}?${queryParams.toString()}`,
                httpRequest.GET,
                null,
                0,
                0,
            );
            yield put(getAllAuditionsSuccess(response));
            yield put(
                setCache({
                    key: queryParams.toString(),
                    value: response,
                    type: CACHE_TYPE.AUDITION,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getAllAuditionsFailed(error));
        }
    }
}

function* getSimilarAuditions({ payload }: { payload: IGetSimilarAuditions }): any {
    const queryParams = new URLSearchParams();
    // Conditionally add parameters
    if (payload.limit) queryParams.append('limit', String(payload.limit));
    if (payload.page) queryParams.append('page', String(payload.page));
    if (payload.audition_type) queryParams.append('audition_type', payload.audition_type);
    if (payload.compensation) queryParams.append('compensation', payload.compensation);
    if (payload.gig_type) queryParams.append('gig_type', payload.gig_type);
    if (payload.premium_audition) queryParams.append('premium_audition', payload.premium_audition);
    const requestUrl = `${getAuditionUrl}?${queryParams.toString()}`;

    let initialResult: any = null;
    const defaultUseCache = yield* isUseCacheEnabled();

    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(
        CACHE_TYPE.SIMILAR_AUDITIONS,
        queryParams.toString(),
    );

    if (cache && cache.key) {
        initialResult = cache.value;
    }

    if (defaultUseCache && initialResult) {
        yield put(getSimilarAuditionsSuccess(initialResult));
    } else {
        try {
            const response = yield call(api, requestUrl, httpRequest.GET, null, 0, 0);
            const { data } = response.data;
            yield put(getSimilarAuditionsSuccess(data));
            yield put(
                setCache({
                    key: queryParams.toString(),
                    value: data,
                    type: CACHE_TYPE.SIMILAR_AUDITIONS,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getSimilarAuditionsFail(error));
        }
    }
}

function* getAuditionsDetails({ payload }: { payload: IGetAudition }): any {
    const requestUrl = `${getAuditionUrl}/${payload.auditionSlug}${payload.params ? payload.params : ''}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.GET, null, 0, 0);
        const { data } = response.data;
        yield put(getAuditionsDetailsSuccess(data));
        yield put(
            getSimilarAuditionsAction({
                audition_type: data.audition_type,
                compensation: data.compensation,
                gig_type: data.gig_type,
                premium_audition: data.premium_audition,
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        const { message } = error.data;
        yield put(getAuditionsDetailsFailed(message));
    }
}

function* applyToAudition({ payload }: { payload: IApplyToAudition }): any {
    const { role } = payload;
    const requestUrl = `${applyToAuditionUrl}/${role}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.POST, { ...payload }, 0, 0);
        const { data } = response;
        yield put(applyToAuditionSuccess(data));
        yield call(showToastMessage, 'Application successful', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        const { message } = error.data || {};
        // TODO Obfusctae this message and use a code instead
        if (message.startsWith('You have reached')) {
            yield put(toggleSubscriptionModal(true));
        }
        yield call(globalErrorHandler, error);
        yield put(applyToAuditionFailed(message || error));
    }
}

function* getAuditionRoles({ payload, resolve, reject }: any): any {
    const requestUrl = `${getAuditionRoleUrl}?audition=${payload}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.GET, null, 0, 0);
        const { data } = response;
        yield put(getAuditionRolesSuccess(data));
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        const { message } = error.data;
        yield put(getAuditionRolesFailed(message || error));
        reject(error);
    }
}

function* getMyApplications({ resolve, reject }: any): any {
    try {
        const response = yield call(api, getMyApplicationsUrl, httpRequest.GET, null, 0, 0);
        const { data } = response;
        yield put(getMyApplicationsSuccess(data));
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getMyApplicationsFailed(error));
        yield call(globalErrorHandler, error);
        reject(error);
    }
}

function* getSavedCollection(): any {
    try {
        const response = yield call(api, getSavedCollectionUrl, httpRequest.GET, null, 0, 0);
        const { data } = response.data;
        yield put(getSavedCollectionSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getSavedCollectionFail(error));
    }
}

// INFO: GET SAVED COLLECTION
function* saveCollection({ payload }: any): any {
    try {
        const response = yield call(api, saveCollectionUrl, httpRequest.POST, payload, 0, 0);
        const { data } = response.data;
        yield put(saveCollectionSuccess(data));
        yield put(getSavedCollectionAction());
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(saveCollectionFail(error));
    }
}

function* deleteSavedCollection({ payload, resolve, reject }: any): any {
    const request = `${saveCollectionUrl}/${payload}`;
    try {
        const response = yield call(api, request, httpRequest.DELETE, {}, 0, 0);
        const { data } = response;
        yield put(deleteSavedCollectionSuccess(payload));
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteSavedCollectionFail(error));
        reject(error);
    }
}

// Ad ID here for payload
function* updateSavedCollection({ payload, resolve, reject }: any): any {
    const request = `${saveCollectionUrl}/${payload.id}`;
    try {
        const response = yield call(api, request, httpRequest.PATCH, payload.body, 0, 0);
        const { data } = response;
        yield put(updateSavedCollectionSuccess(data));
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateSavedCollectionFail(error));
        reject(error);
    }
}

function* manageAuditionWithSubmissions({
    payload,
    resolve,
    reject,
}: {
    payload: IManageAuditionParams;
    resolve: (response: IManageAuditionResponse) => void;
    reject: (error: any) => void;
}): Generator<any, void, IManageAuditionResponse> {
    const { auditionId, page = 1, limit = 9, status, sort = 'newest', search, roleId } = payload;
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(page));
    queryParams.append('limit', String(limit));
    if (status) queryParams.append('status', status);
    queryParams.append('sort', sort);
    if (search) queryParams.append('search', search);
    if (roleId) queryParams.append('roleId', roleId);

    const requestUrl = `${getAuditionUrl}/${auditionId}/with-submissions?${queryParams.toString()}`;

    try {
        const response = yield call(api, requestUrl, httpRequest.GET, null, 0, 0);
        yield put(manageAuditionSuccess(response));

        if (resolve) resolve(response);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(manageAuditionFail(error));
        if (reject) reject(error);
    }
}

/** Interfaces */
interface TaskAction extends Action, IWithdrawApplication {
    type: string;
    payload: any;
    resolve: any;
    reject: any;
}

/** **************** WATCHERS ************************** */

function* getAllAuditionsWatcher() {
    yield takeLatest<any>(types.GET_ALL_AUDITIONS, getAllAuditions);
}

function* getAuditionsDetailsWatcher() {
    yield takeLatest<any>(types.GET_AUDITION_DETAIL, getAuditionsDetails);
}

function* applyToAuditionWatcher() {
    yield takeLatest<any>(types.APPLY_TO_AUDITION, applyToAudition);
}

function* getAuditionrolesWatcher() {
    yield takeLatest(types.GET_ROLES, getAuditionRoles);
}

function* getMyApplicationsWatcher() {
    yield takeLatest(types.GET_MY_APPLICATIONS, getMyApplications);
}

function* getSavedCollectionWatcher() {
    yield takeLatest(types.GET_SAVED_COLLECTION, getSavedCollection);
}

function* saveCollectionWatcher() {
    yield takeLatest(types.SAVE_COLLECTION, saveCollection);
}

function* deleteSavedCollectionWatcher() {
    yield takeLatest(types.DELETE_SAVED_COLLECTION, deleteSavedCollection);
}

function* updateSavedCollectionWatcher() {
    yield takeLatest(types.UPDATE_SAVED_COLLECTION, updateSavedCollection);
}

function* createAuditionWatcher() {
    yield takeLatest<any>(types.CREATE_AUDITION, createAudition);
}

function* createAuditionRoleWatcher() {
    yield takeLatest<TaskAction>(types.CREATE_ROLE, createAuditionRole);
}

function* updateAuditionWatcher() {
    yield takeLatest<any>(types.UPDATE_AUDITION, updateAudition);
}

function* publishAuditionWatcher() {
    yield takeLatest(types.PUBLISH_AUDITION, publishAudition);
}
function* unPublishAuditonWatcher() {
    yield takeLatest(types.UNPUBLISH_AUDITION, unPublishAudition);
}

function* deleteAuditionWatcher() {
    yield takeLatest(types.DELETE_AUDITION, deleteAudition);
}

function* deleteAuditionRoleWatcher() {
    yield takeLatest(types.DELETE_ROLE, deleteAuditionRole);
}

function* getAllAuditionsApplicationWatcher() {
    yield takeLatest(types.GET_ALL_APPLICATION_FOR_AUDITION, getAllApplicationsForAudition);
}

function* sortApplicationWatcher() {
    yield takeLatest(types.SORT_APPLICATION, sortApplication);
}

function* filterAuditionWatcher() {
    yield takeLatest(types.FILTER_AUDITION, filterAudition);
}

function* advancedSearchAuditionWatcher() {
    yield takeLatest(types.ADVANCED_AUDITION_SEARCH, advancedSearchAudition);
}

function* loadingMoreAuditionsWatcher() {
    yield takeLatest(types.LOAD_MORE_AUDITIONS as any, loadingMoreAuditions);
}

function* withdrawApplicationWatcher() {
    yield takeLatest<TaskAction>(types.WITHDRAW_APPLICATION, withdrawApplication);
}

function* getSimilarAuditionWatcher() {
    yield takeLatest<TaskAction>(types.GET_SIMILAR_AUDITION, getSimilarAuditions);
}

function* createCastingDirectorWatcher() {
    yield takeLatest<TaskAction>(types.CREATE_CASTING_DIRECTOR, createCastingDirector);
}

function* getMyCastingDirectorsWatcher() {
    yield takeLatest<TaskAction>(types.GET_CASTING_DIRECTOR, getMyCastingDirectors);
}

function* updateCastingDirectorWatcher() {
    yield takeLatest<TaskAction>(types.UPDATE_CASTING_DIRECTOR, updateCastingDirector);
}

function* deleteCastingDirectorWatcher() {
    yield takeLatest<TaskAction>(types.DELETE_CASTING_DIRECTOR, deleteCastingDirector);
}

function* manageAuditionWithSubmissionsWatcher() {
    yield takeLatest<TaskAction>(types.MANAGE_AUDITION, manageAuditionWithSubmissions);
}

function* updateAuditionRoleWatcher() {
    yield takeLatest<TaskAction>(types.UPDATE_ROLE, updateAuditionRole);
}

export default function* auditionSaga() {
    yield all([
        getAllAuditionsWatcher(),
        getAuditionsDetailsWatcher(),
        applyToAuditionWatcher(),
        getAuditionrolesWatcher(),
        getMyApplicationsWatcher(),
        getSavedCollectionWatcher(),
        saveCollectionWatcher(),
        deleteSavedCollectionWatcher(),
        updateSavedCollectionWatcher(),
        createAuditionWatcher(),
        createAuditionRoleWatcher(),
        updateAuditionWatcher(),
        publishAuditionWatcher(),
        unPublishAuditonWatcher(),
        deleteAuditionWatcher(),
        deleteAuditionRoleWatcher(),
        getAllAuditionsApplicationWatcher(),
        sortApplicationWatcher(),
        filterAuditionWatcher(),
        advancedSearchAuditionWatcher(),
        loadingMoreAuditionsWatcher(),
        withdrawApplicationWatcher(),
        getSimilarAuditionWatcher(),
        createCastingDirectorWatcher(),
        getMyCastingDirectorsWatcher(),
        updateCastingDirectorWatcher(),
        deleteCastingDirectorWatcher(),
        manageAuditionWithSubmissionsWatcher(),
        updateAuditionRoleWatcher(),
    ]);
}
